import type { InnogyComponentRendering } from '@core/jss-models';
import { getFieldValue } from '@core/jss-utils';
import {
  mapScStoreProduct,
  type ScStoreProductData,
} from '@innogy/common-ui/products';
import type { ProgressiveFormStateImplementor } from '@innogy/progressive-ngrx-forms';
import {
  activateProgressiveFormStepOnSubmissionOf,
  createProgressiveFormGroupState,
  createProgressiveNgrxFormReducerWithFormStateUpdate,
} from '@innogy/progressive-ngrx-forms';
import type { DeviceInfo } from '@innogy/shared/responsive-tools';
import { validateSequential } from '@innogy/utils-deprecated';
import type { StoreProductData } from '@legacy/common-ui-models';
import type { Action } from '@ngrx/store';
import { on } from '@ngrx/store';
import { updateGroup } from 'ngrx-forms';
import { required } from 'ngrx-forms/validation';

import { ORDER_ADDRESS_FORM_ID } from '../address';
import { addOrderRenderingAction } from '../order.actions';
import type { OrderState } from '../order.state';
import { initOrderCrossSell } from './order-cross-sell.actions';

export const ORDER_CROSS_SELL_FORM_ID: keyof OrderState = 'orderCrossSellForm';
export const hasSelectedProductFormControlId = `${ORDER_CROSS_SELL_FORM_ID}.hasSelectedProduct`;

export interface OrderCrossSellFormValues {
  hasSelectedProduct: boolean | undefined;
}

export interface OrderCrossSellState
  extends ProgressiveFormStateImplementor<OrderCrossSellFormValues> {
  rendering?: InnogyComponentRendering;
  product?: StoreProductData;
  previouslyEnabled: boolean;
  enabled: boolean;
}

export const initialCrossSellProgressiveFormState =
  createProgressiveFormGroupState<OrderCrossSellFormValues>(
    ORDER_CROSS_SELL_FORM_ID,
    {
      hasSelectedProduct: false,
    }
  );

export const initialOrderCrossSellState: OrderCrossSellState = {
  progressiveForm: initialCrossSellProgressiveFormState,
  rendering: undefined,
  product: undefined,
  previouslyEnabled: false,
  enabled: false,
};

const validateForm = (state: OrderCrossSellState) =>
  updateGroup<OrderCrossSellFormValues>({
    hasSelectedProduct: validateSequential(required),
  })(state.progressiveForm.formState);

const shouldEnableCrossSell = (
  state: OrderCrossSellState,
  device: DeviceInfo
): boolean => {
  const featureEnabled = getFieldValue<boolean>(
    state.rendering,
    'EnableCrossSellStep',
    false
  );
  if (!featureEnabled) {
    return false;
  }

  // enable always when the user has already interacted with cross sell before
  if (state.progressiveForm.formState.value.hasSelectedProduct) {
    return true;
  }

  // disable when not on desktop and feature is only enabled for desktop
  const onlyOnDesktop = getFieldValue<boolean>(
    state.rendering,
    'CrossSellDesktopOnly',
    false
  );
  if (onlyOnDesktop && !device.isDesktop) {
    return false;
  } else {
    return true;
  }
};

const _reducer = createProgressiveNgrxFormReducerWithFormStateUpdate(
  initialOrderCrossSellState,
  validateForm,
  on(initOrderCrossSell, (state, { deviceInfo }) => ({
    ...state,
    previouslyEnabled: state.enabled,
    enabled: shouldEnableCrossSell(state, deviceInfo),
  })),
  on(addOrderRenderingAction, (state, action) => {
    const crossSellProduct = action.rendering.fields?.[
      'CrossSellProduct'
    ] as unknown as ScStoreProductData;
    return {
      ...state,
      rendering: action.rendering,
      product: crossSellProduct
        ? mapScStoreProduct(crossSellProduct)
        : undefined,
    };
  }),
  activateProgressiveFormStepOnSubmissionOf(ORDER_ADDRESS_FORM_ID)
);

export function orderCrossSellReducer(
  state: OrderCrossSellState = initialOrderCrossSellState,
  action: Action
): OrderCrossSellState {
  return _reducer(state, action);
}
